// API Key management module
// Used by AdminPage to generate, list, revoke, and rotate API keys

import { supabase } from '../lib/supabase';

// ============================================
// CRYPTO HELPERS (browser-side)
// ============================================

/** Generate a cryptographically random hex string */
export function generateRandomHex(bytes: number = 32): string {
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 hash a string and return hex */
export async function sha256Hash(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================
// API KEY INTERFACES
// ============================================

export interface ApiKeyRecord {
  id: string;
  name: string;
  created_by: string;
  created_at: string;
  expires_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface ApiUsageLog {
  id: string;
  api_key_id: string;
  endpoint: string;
  status_code: number;
  request_summary: string | null;
  created_at: string;
}

export interface GenerateKeyResult {
  success: boolean;
  error?: string;
  /** The plaintext API key, shown ONCE on creation */
  apiKey?: string;
  /** The key record from the database */
  keyRecord?: ApiKeyRecord;
  /** The plaintext recovery code, shown ONCE on first key creation */
  recoveryCode?: string;
}

// ============================================
// API KEY OPERATIONS
// ============================================

/** Get all API keys (metadata only, never returns the actual key) */
export async function getApiKeys(): Promise<{ success: boolean; data?: ApiKeyRecord[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('api_keys')
      .select('id, name, created_by, created_at, expires_at, last_used_at, is_active')
      .order('created_at', { ascending: false });

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Error fetching API keys:', err);
    return { success: false, error: 'Failed to fetch API keys.' };
  }
}

/** Generate a new API key. Returns the plaintext key ONCE. */
export async function generateApiKey(name: string): Promise<GenerateKeyResult> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated.' };
    }

    // Generate the key (64 hex chars = 32 bytes)
    const rawKey = generateRandomHex(32);
    const keyHash = await sha256Hash(rawKey);
    const expiresAt = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString();

    // Insert hashed key into database
    const { data: keyRecord, error: insertError } = await supabase
      .from('api_keys')
      .insert({
        key_hash: keyHash,
        name: name || 'Unnamed Key',
        created_by: user.id,
        expires_at: expiresAt,
        is_active: true,
      })
      .select('id, name, created_by, created_at, expires_at, last_used_at, is_active')
      .single();

    if (insertError || !keyRecord) {
      console.error('Error creating API key:', insertError);
      return { success: false, error: 'Failed to create API key.' };
    }

    // Check if this is the first-ever key. If so, generate a recovery code
    let recoveryCode: string | undefined;
    const { count } = await supabase
      .from('recovery_codes')
      .select('id', { count: 'exact', head: true })
      .eq('created_by', user.id);

    if (count === 0) {
      // First key ever, generate recovery code
      recoveryCode = generateRandomHex(16); // 32 hex chars
      const recoveryHash = await sha256Hash(recoveryCode);

      await supabase.from('recovery_codes').insert({
        code_hash: recoveryHash,
        created_by: user.id,
        is_active: true,
      });
    }

    return {
      success: true,
      apiKey: rawKey,
      keyRecord,
      recoveryCode,
    };
  } catch (err) {
    console.error('Error generating API key:', err);
    return { success: false, error: 'Internal error generating API key.' };
  }
}

/** Revoke (deactivate) an API key */
export async function revokeApiKey(keyId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('api_keys')
      .update({ is_active: false })
      .eq('id', keyId);

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err) {
    console.error('Error revoking API key:', err);
    return { success: false, error: 'Failed to revoke API key.' };
  }
}

/** 
 * Rotate an API key. Deactivates the old key and creates a new one.
 * Requires password re-authentication for security.
 */
export async function rotateApiKey(
  oldKeyId: string, 
  password: string,
  newName?: string
): Promise<GenerateKeyResult> {
  try {
    // Re-authenticate the admin
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      return { success: false, error: 'Not authenticated.' };
    }

    // Verify password by signing in again
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password,
    });

    if (authError) {
      return { success: false, error: 'Password verification failed. Please try again.' };
    }

    // Deactivate the old key
    const revokeResult = await revokeApiKey(oldKeyId);
    if (!revokeResult.success) {
      return { success: false, error: revokeResult.error };
    }

    // Generate a new key
    return await generateApiKey(newName || 'Rotated Key');
  } catch (err) {
    console.error('Error rotating API key:', err);
    return { success: false, error: 'Internal error rotating API key.' };
  }
}

/** Get usage logs for a specific API key */
export async function getApiKeyUsageLogs(
  keyId: string, 
  limit: number = 50
): Promise<{ success: boolean; data?: ApiUsageLog[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('api_usage_logs')
      .select('*')
      .eq('api_key_id', keyId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('Error fetching usage logs:', err);
    return { success: false, error: 'Failed to fetch usage logs.' };
  }
}

/** Check if a recovery code exists for the current user */
export async function hasRecoveryCode(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { count } = await supabase
      .from('recovery_codes')
      .select('id', { count: 'exact', head: true })
      .eq('created_by', user.id)
      .eq('is_active', true);

    return (count || 0) > 0;
  } catch {
    return false;
  }
}
